<template>
    <b-form>
        <b-alert
            show
            variant="warning"
            v-if="data.isDefaultProvider && data.bundle_id.length > 0"
        >
            <span id="dialog-alert-message">
                {{ CLINIC_PROVIDER_ALERT.LAB_RADIOLOGY_BUNDLE }}
            </span>
        </b-alert>
        <b-form-group :label-for="`bundle_id`" :label-cols="3" label="Bundle:">
            <v-select
                id="bundle_id"
                name="bundle_id"
                ref="vselect"
                :options="bundle_options_exclude"
                v-model="data.bundle_id"
                v-validate="'required'"
                placeholder="Select bundle (required)"
                multiple
                label="name"
                :class="{ 'border-danger': errors.has('bundle_id') }"
            />
        </b-form-group>
        <b-form-group
            label="Invoice:"
            class
            label-cols="3"
            label-for="bill_to_invoice"
        >
            <b-form-select
                id="bill_to_invoice"
                v-model="data.bill_to_invoice"
                :options="invoices"
                name="bill_to_invoice"
            >
                <option :value="-1">New Invoice</option>
            </b-form-select>
        </b-form-group>
        <b-form-group
            label="Provider:"
            class
            label-cols="3"
            label-for="staff_id"
        >
            <select
                id="staff_id"
                v-model="data.staff_id"
                class="form-control"
                v-validate="'excluded:0'"
                :class="{ 'border-danger': errors.has('staff_id') }"
                name="staff_id"
            >
                <option value="0">Provider (required)</option>
                <option
                    v-for="provider in providers"
                    :key="provider.id"
                    :value="provider.id"
                    >{{ provider.providerName }}</option
                >
            </select>
        </b-form-group>
        <b-form-group
            v-if="!isSoap"
            class="medical-note"
            :label-for="`soap`"
            :label-cols="3"
            label="Medical Note:"
        >
            <b-form-select
                id="soap"
                v-model="data.soap"
                :options="medicalNotes"
                name="soap"
            ></b-form-select>
        </b-form-group>
        <b-form-group :label-for="`start`" :label-cols="3" :label="'Start:'">
            <date-picker
                id="startDate"
                name="startDate"
                :config="datePickerOptions"
                v-validate="'required'"
                @dp-show="showDatePicker"
                v-model="data.startDate"
                placeholder="Date (required)"
                :class="{ 'border-danger': errors.has('startDate') }"
            />
        </b-form-group>
    </b-form>
</template>
<style></style>
<script>
import { get, forEach, union, find } from "lodash";
import vSelect from "vue-select";
import CLINICPROVIDER from "@/constants/clinic-provider";
import joinPaymentInvoiceSocket from "@/mixins/join-payment-invoice-socket.js";
export default {
    mixins: [joinPaymentInvoiceSocket],
    components: { vSelect },
    props: {
        data: {
            type: Object,
            default: () => {}
        },
        parentLoading: {
            type: Boolean,
            default: false
        },
        isSoap: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            medicalNotes: [],
            loading: false,
            bundles: [],
            providers: [],
            formError: [],
            invoices: [],
            CLINIC_PROVIDER_ALERT: CLINICPROVIDER.CLINIC_PROVIDER_ALERT
        };
    },
    computed: {
        bundle_options_exclude() {
            return _.difference(this.bundles, this.data.bundle_id);
        },
        datePickerOptions() {
            return {
                format: this.getDateFormat(),
                widgetParent: "#addBundleModal"
            };
        }
    },
    watch: {
        "data.staff_id"(value) {
            if (value !== "") {
                if (this.providerCount === 0) {
                    this.data.staff_id = 0;
                } else {
                    this.setStaffId(this.providers);
                }
            }
        },
        "data.bill_to_invoice"(value) {
            if (value !== -3) {
                this.joinPaymentInvoice(value);
            }
        }
    },
    mounted() {
        this.loading = true;
        this.$nextTick(() => {
            this.$emit("beforeload");

            let process = [
                this.getBundle(),
                this.getProviders(),
                this.getBilling(this.data.client_id)
            ];

            if (!this.isSoap) {
                // For soap page and new bundle no need to has medical note field
                process.push(this.getMedicalNote(this.data.patient_id));
            }

            if (!this.isSoap) {
                // From soap profile will be using the soap.date
                process.push(this.setDefaultDate());
            }

            this.$emit("loading");
            Promise.all(process).then(() => {
                this.$emit("loaded");
            });

            this.$validator.reset();
            this.loading = false;
        });
    },
    methods: {
        showDatePicker(event) {
            // fix position of the date picker
            this._datePickerShow(event);
        },
        setDefaultDate() {
            this.data.startDate = moment().tz(this._timeZone);
        },
        getBundle() {
            let self = this;
            return this.$http
                .get(process.env.ACCOUNT_SERVICE + "template/bundle")
                .then(function (response) {
                    self.bundles = response.data.response.resources;
                });
        },
        getProviders() {
            let self = this;
            return this.$http
                .get(process.env.BARRACUDA + "actor/staff/business/providers")
                .then(function (response) {
                    self.providers = get(
                        response,
                        "data.response.resource.data",
                        []
                    );

                    self.providers = _.map(self.providers, element => ({
                        ...element,
                        id: element.actor.id,
                        providerName:
                            element.actor.firstname +
                            " " +
                            element.actor.lastname
                    }));

                    // --- sort by provider name
                    self.providers = _.sortBy(self.providers, ["providerName"]);
                    let defaultProvider = _.find(
                        self.providers,
                        "is_default_provider"
                    );

                    if (self.providers.length === 0) {
                        self.data.staff_id = "0";
                        self.providerCount = 0;
                    } else {
                        self.setStaffId(self.providers);
                    }
                });
        },
        setStaffId(providerOptions) {
            let self = this;
            let existingStaff = find(providerOptions, [
                "id",
                self.data.staff_id
            ]);
            let defaultClinicProvider = find(providerOptions, [
                "is_default_provider",
                true
            ]);
            if (!existingStaff) {
                let currentStaff = find(providerOptions, [
                    "id",
                    this.$root.currentActor.id
                ]);
                // default the provider if cannot find provider
                if (defaultClinicProvider && !currentStaff) {
                    self.data.isDefaultProvider = true;
                    self.data.staff_id = defaultClinicProvider.id;
                } else {
                    self.data.staff_id = currentStaff
                        ? currentStaff.id
                        : providerOptions[0].id;
                }
            } else {
                if (defaultClinicProvider) {
                    self.data.isDefaultProvider =
                        self.data.staff_id == defaultClinicProvider.id;
                }
            }
        },
        getMedicalNote(patientID) {
            let self = this;
            let medicalNote = [];
            if (patientID) {
                return this.$http
                    .get(
                        process.env.BARRAMUNDI +
                            "record/medical-note-list/" +
                            patientID
                    )
                    .then(function (response) {
                        if (response.data.response.resources) {
                            forEach(response.data.response.resources, function (
                                value
                            ) {
                                let name = "";
                                switch (value.type) {
                                    case 0:
                                    case 1:
                                    case 2:
                                        name =
                                            "Medical Note" +
                                            " (" +
                                            self
                                                ._toDateTime(value.date)
                                                .toFormat(self._dateFormat) +
                                            ")";
                                        break;
                                    default:
                                        if (value.template_id) {
                                            name =
                                                value.template_name +
                                                " (" +
                                                self
                                                    ._toDateTime(value.date)
                                                    .toFormat(
                                                        self._dateFormat
                                                    ) +
                                                ")";
                                        }
                                }
                                if (value.title) {
                                    name =
                                        value.title +
                                        " (" +
                                        self
                                            ._toDateTime(value.date)
                                            .toFormat(self._dateFormat) +
                                        ")";
                                }

                                medicalNote.push({
                                    value: value.id,
                                    text: name,
                                    date: value.date
                                });
                            });

                            self.medicalNotes = union(
                                [{ value: -1, text: "No Medical Note" }],
                                medicalNote
                            );
                        }
                    });
            }
        },
        getBilling(clientID) {
            let self = this;
            if (clientID) {
                return this.$http
                    .get(
                        process.env.ACTOR_SERVICE +
                            "client/" +
                            clientID +
                            "/invoice"
                    )
                    .then(async function (response) {
                        if (response.data.response.resource) {
                            self.invoices = response.data.response.resource.map(
                                invoice => ({
                                    value: invoice.id,
                                    text:
                                        "Invoice #" +
                                        invoice.index +
                                        " (" +
                                        moment(invoice.start.date).format(
                                            self.datePickerOptions.format
                                        ) +
                                        ")"
                                })
                            );
                            self.data.bill_to_invoice =
                                self.invoices.length > 0
                                    ? self.invoices[0].value
                                    : -1;

                            if (self.isSoap) {
                                let activeSoapInvoice = await self._activeSoapInvoice(
                                    self.$route.params.id
                                );
                                if (activeSoapInvoice) {
                                    self.data.bill_to_invoice =
                                        activeSoapInvoice.id;
                                }
                            }
                        }
                    });
            } else {
                self.data.bill_to_invoice = -1;
            }
        }
    }
};
</script>
